import { useState } from "react";

import {
    ChatDots,
    CheckCircle,
    CloseCircle,
    AltArrowDown,
    AltArrowUp,
} from "@solar-icons/react/ssr";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { request } from "@/Api";
import { InternalLink } from "@/common/CommonComponents";
import DeleteRowButton from "@/common/DeleteRowButton";
import { NumericFormatCustom } from "@/common/StyledComponents";
import { Subtitle } from "@/common/typography";
import { CustomRow } from "@/table/CustomTable";
import { getCurrencySymbol, formattedCurrency } from "@/utils";

const BillRow = ({ item, items, setItems }) => {
    const [open, setOpen] = useState(false);
    const [focusedBillId, setFocusedBillId] = useState(null);

    const isForecast = item.bill_type == "Forecast";

    const handleSwitchChange = (event) => {
        request
            .put(`bills/${item.id}`, {
                ...item,
                sent: event.target.checked,
            })
            .then((response) => {
                setItems(
                    items.map((bill) => {
                        return bill.id == item.id
                            ? { ...item, sent: response.data.sent }
                            : bill;
                    }),
                );
            });
    };

    const handleBillDeleteConfirm = () => {
        const baseEndpoint = isForecast ? "forecast-bills" : "bills";

        request.delete(`${baseEndpoint}/${item.id}`).then(() => {
            setItems(
                items.filter((bill) => {
                    return bill.id != item.id;
                }),
            );
        });
    };

    const handleInputBlur = (fieldName) => (event) => {
        const value = event.target.value.replace(/,/g, "");
        request
            .put(`bills/${item.id}`, {
                ...item,
                [fieldName]: value || 0,
            })
            .then(() => {
                setItems(
                    items.map((bill) => {
                        return bill.id == item.id
                            ? { ...bill, [fieldName]: value }
                            : bill;
                    }),
                );
            });
    };

    return (
        <>
            <CustomRow>
                <TableCell>
                    <IconButton
                        disabled={!item.notes}
                        size="small"
                        onClick={() => setOpen(!open)}
                        aria-label="expand row"
                    >
                        {open ? <AltArrowUp /> : <AltArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {item.bill_date ? item.bill_date.split("T")[0] : null}
                </TableCell>
                <TableCell align="left">{item.docket}</TableCell>
                <TableCell align="center">
                    <Chip label={item.bill_type} />
                </TableCell>
                <TableCell align="center">{item.certainty}</TableCell>
                <TableCell align="right">{`${(item.quoted_percentage * 100).toFixed()}%`}</TableCell>
                <TableCell align="right">
                    {formattedCurrency(
                        item.estimated_amount,
                        item.currency,
                        false,
                        2,
                    )}
                </TableCell>

                <TableCell width={160} align="right">
                    {isForecast ? (
                        "-"
                    ) : item.sent ? (
                        <Tooltip title="This bill is already sent">
                            <div
                                style={{ cursor: "not-allowed", color: "grey" }}
                            >
                                {formattedCurrency(
                                    item.actual_amount || 0,
                                    item.currency,
                                    false,
                                    2,
                                )}
                            </div>
                        </Tooltip>
                    ) : focusedBillId == item.id ? (
                        <TextField
                            size="small"
                            autoFocus
                            onBlur={handleInputBlur("actual_amount")}
                            onKeyDown={(e) => {
                                e.key != "Enter" ||
                                    handleInputBlur("actual_amount")(e);
                            }}
                            value={item.actual_amount || ""}
                            InputProps={{
                                startAdornment: getCurrencySymbol(
                                    item.currency,
                                ),
                                inputComponent: NumericFormatCustom,
                            }}
                            inputProps={{
                                style: { textAlign: "right" },
                            }}
                        />
                    ) : (
                        <div
                            onClick={() => {
                                setFocusedBillId(item.id);
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            {formattedCurrency(
                                item.actual_amount || 0,
                                item.currency,
                                false,
                                2,
                            )}
                        </div>
                    )}
                </TableCell>

                <TableCell align="left">
                    <InternalLink
                        href={`/projects/${item.project_id}`}
                        color="primary"
                    >
                        {item.project_name}
                    </InternalLink>
                </TableCell>
                <TableCell align="left">{item.project_status}</TableCell>
                <TableCell align="left">{item.client_name}</TableCell>
                <TableCell align="center">
                    {isForecast ? (
                        "-"
                    ) : (
                        <IconButton
                            disabled={!item.notes}
                            size="small"
                            onClick={() => setOpen(!open)}
                            aria-label="expand row"
                        >
                            <ChatDots />
                        </IconButton>
                    )}
                </TableCell>
                <TableCell align="center">
                    {isForecast ? (
                        "-"
                    ) : item.signed ? (
                        <CheckCircle color="green" size={20} />
                    ) : (
                        <CloseCircle color="red" size={20} />
                    )}
                </TableCell>
                <TableCell align="center">
                    {isForecast ? (
                        "-"
                    ) : (
                        <Switch
                            checked={item.sent ? true : false}
                            onChange={handleSwitchChange}
                        />
                    )}
                </TableCell>
                <TableCell align="left">
                    <DeleteRowButton
                        iconSize="small"
                        iconButtonSize="small"
                        handleDeleteConfirm={handleBillDeleteConfirm}
                    />
                </TableCell>
            </CustomRow>
            {item.notes ? (
                <TableRow>
                    <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={15}
                    >
                        <Collapse
                            sx={{ padding: "20px" }}
                            in={open}
                            timeout="auto"
                            unmountOnExit
                        >
                            <Subtitle style={{ marginBottom: "16px" }}>
                                Notes
                            </Subtitle>
                            {item.notes}
                        </Collapse>
                    </TableCell>
                </TableRow>
            ) : null}
        </>
    );
};

export default BillRow;
